import type {
  BosObjectStorageConfig,
  BaseObjectStorageConfig,
  NormalObjectStorageConfig,
  ObsObjectStorageConfig,
  ObjectStorageConfig,
  ObjectStorageType,
  OssObjectStorageConfig,
  QiniuObjectStorageConfig,
  S3ObjectStorageConfig,
  UploadParams,
  CancelHandle,
  UploadProgress,
  UploadResult,
  StorageAdapter,
} from './type';
import { createStorageAdapter } from './service/adapterFactory';

/**
 * 浏览器端统一对象存储客户端。
 *
 * 该类保持对外 API 稳定，并将厂商初始化、上传等差异委托给内部适配器。
 */
export class ObjectStorage<TInstance = unknown, TSdkObject = unknown> {
  private readonly adapter: StorageAdapter<TInstance>;
  /** 云厂商上传时为已初始化的 SDK 实例；normal 上传时为 undefined。 */
  readonly instance: TInstance;
  /** 当前存储后端使用 bucket 时的 bucket 名称。 */
  readonly bucketName: string | undefined;
  /** 当前选择的上传后端类型。 */
  readonly uploadType: ObjectStorageType;

  /**
   * 根据厂商配置创建存储客户端。
   */
  constructor(config: ObjectStorageConfig<TSdkObject>) {
    if (!config?.type) {
      throw new Error('ObjectStorage: config.type is required');
    }

    this.adapter = createStorageAdapter<TInstance, TSdkObject>(config);
    this.instance = this.adapter.instance;
    this.bucketName = 'bucketName' in config ? config.bucketName : undefined;
    this.uploadType = config.type;
  }

  /**
   * 使用当前配置的后端上传文件。
   */
  upload(params: UploadParams): Promise<UploadResult> {
    return this.adapter.upload(params);
  }

  /**
   * 当适配器支持取消时，取消厂商侧上传任务。
   */
  cancel(): void {
    this.adapter.cancel?.();
  }
}

export type {
  BosObjectStorageConfig,
  BaseObjectStorageConfig,
  NormalObjectStorageConfig,
  ObsObjectStorageConfig,
  ObjectStorageConfig,
  ObjectStorageType,
  OssObjectStorageConfig,
  QiniuObjectStorageConfig,
  S3ObjectStorageConfig,
  UploadParams,
  UploadResult,
  CancelHandle,
  UploadProgress,
  StorageAdapter,
};
